import React, { useState, useEffect } from "react";
import {
  dividendApi,
  DividendEvent,
  TradingDay,
  MonthSummary,
} from "../services/api";
import "./DividendCalendar.css";

interface DividendCalendarProps {
  onDateSelect?: (date: Date) => void;
}

interface DateRange {
  start: Date;
  end: Date;
}

const DividendCalendar: React.FC<DividendCalendarProps> = ({
  onDateSelect,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dividends, setDividends] = useState<DividendEvent[]>([]);
  const [monthSummary, setMonthSummary] = useState<MonthSummary | null>(null);
  const [selectedRange, setSelectedRange] = useState<DateRange | null>(null);
  const [rangeDividends, setRangeDividends] = useState<DividendEvent[]>([]);
  const [rangeTrades, setRangeTrades] = useState<TradingDay[]>([]);
  const [prices, setPrices] = useState<
    Record<string, { symbol: string; date: string; price: number }>
  >({});
  const [loading, setLoading] = useState(false);
  const [showExDates, setShowExDates] = useState(true);
  const [showBuyOrders, setShowBuyOrders] = useState(true);
  const [showSellOrders, setShowSellOrders] = useState(true);
  const [showEstimates, setShowEstimates] = useState(false); // Default: hide estimates

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    loadMonthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  useEffect(() => {
    if (selectedRange) {
      loadRangeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange, showEstimates]);

  const loadMonthData = async () => {
    setLoading(true);
    try {
      // Request today through next 2 months (rest of current month + full next month + buffer)
      const [divs, summary] = await Promise.all([
        dividendApi.getDividends(2, true),  // 2 months ahead, future only
        dividendApi.getMonthlyTrades(year, month),
      ]);

      setDividends(divs);
      setMonthSummary(summary);
    } catch (error) {
      console.error("Error loading month data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadRangeData = async () => {
    if (!selectedRange) return;

    try {
      const start = selectedRange.start;
      const end = selectedRange.end;

      // Get all days in range
      const days: Date[] = [];
      const current = new Date(start);
      while (current <= end) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      // Fetch trades for each day in range
      const tradesPromises = days.map((day) => {
        const dateStr = day.toISOString().split("T")[0];
        return dividendApi.getTradesForDate(dateStr);
      });

      const tradesResults = await Promise.all(tradesPromises);
      const validTrades = tradesResults.filter((t) => t.total_trades > 0);
      setRangeTrades(validTrades);

      // Get dividends in range
      const startStr = start.toISOString().split("T")[0];
      const endStr = end.toISOString().split("T")[0];
      const divs = dividends.filter(
        (d) => {
          if (d.ex_date < startStr || d.ex_date > endStr) return false;
          if (!showEstimates && d.source === 'yfinance_estimated') return false;
          return true;
        }
      );
      setRangeDividends(divs);

      // Fetch historical prices for all symbols in range
      const priceItems: { symbol: string; date: string }[] = [];

      // Add dividend events
      divs.forEach((div) => {
        priceItems.push({ symbol: div.symbol, date: div.ex_date });
      });

      // Add trade orders (use ex_date for the price)
      validTrades.forEach((day) => {
        day.buy_orders.forEach((order) => {
          priceItems.push({ symbol: order.symbol, date: order.ex_date });
        });
        day.sell_orders.forEach((order) => {
          priceItems.push({ symbol: order.symbol, date: order.ex_date });
        });
      });

      // Fetch prices if we have items
      if (priceItems.length > 0) {
        const pricesData = await dividendApi.getHistoricalPrices(priceItems);
        setPrices(pricesData);
      }
    } catch (error) {
      console.error("Error loading range data:", error);
    }
  };

  const handleDateClick = (date: Date, event: React.MouseEvent) => {
    if (event.shiftKey && selectedRange) {
      // Shift-click creates range from current selection start to new date
      const start = date < selectedRange.start ? date : selectedRange.start;
      const end = date > selectedRange.start ? date : selectedRange.start;
      setSelectedRange({ start, end });
    } else {
      // Regular click selects single date
      setSelectedRange({ start: date, end: date });
    }

    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  const clearSelection = () => {
    setSelectedRange(null);
    setRangeDividends([]);
    setRangeTrades([]);
  };

  const getDividendsForDate = (date: Date): DividendEvent[] => {
    const dateStr = date.toISOString().split("T")[0];
    return dividends.filter((div) => {
      if (div.ex_date !== dateStr) return false;
      // Filter out estimates if toggle is off (but keep yahoo_announced)
      if (!showEstimates && div.source === 'yfinance_estimated') return false;
      return true;
    });
  };

  const getTradingDayInfo = (
    date: Date
  ): {
    announcedBuyCount: number;
    announcedSellCount: number;
    estimatedBuyCount: number;
    estimatedSellCount: number;
  } => {
    if (!monthSummary?.calendar) return {
      announcedBuyCount: 0,
      announcedSellCount: 0,
      estimatedBuyCount: 0,
      estimatedSellCount: 0
    };

    const dateStr = date.toISOString().split("T")[0];
    const day = monthSummary.calendar.find((d) => d.date === dateStr);

    if (!day) return {
      announcedBuyCount: 0,
      announcedSellCount: 0,
      estimatedBuyCount: 0,
      estimatedSellCount: 0
    };

    // Get symbol+ex_date pairs for estimated dividends only
    const estimatedDividends = new Set(
      dividends
        .filter(d => d.source === 'yfinance_estimated')
        .map(d => `${d.symbol}:${d.ex_date}`)
    );

    // Separate orders into announced and estimated
    const announcedBuyOrders = day.buy_orders.filter(order =>
      !estimatedDividends.has(`${order.symbol}:${order.ex_date}`)
    );
    const announcedSellOrders = day.sell_orders.filter(order =>
      !estimatedDividends.has(`${order.symbol}:${order.ex_date}`)
    );
    const estimatedBuyOrders = day.buy_orders.filter(order =>
      estimatedDividends.has(`${order.symbol}:${order.ex_date}`)
    );
    const estimatedSellOrders = day.sell_orders.filter(order =>
      estimatedDividends.has(`${order.symbol}:${order.ex_date}`)
    );

    return {
      announcedBuyCount: announcedBuyOrders.length,
      announcedSellCount: announcedSellOrders.length,
      estimatedBuyCount: showEstimates ? estimatedBuyOrders.length : 0,
      estimatedSellCount: showEstimates ? estimatedSellOrders.length : 0,
    };
  };

  const isDateInRange = (date: Date): boolean => {
    if (!selectedRange) return false;
    return date >= selectedRange.start && date <= selectedRange.end;
  };

  const renderCalendarDays = () => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const isToday = date.toDateString() === new Date().toDateString();
      const isInRange = isDateInRange(date);
      const isRangeStart =
        selectedRange?.start.toDateString() === date.toDateString();
      const isRangeEnd =
        selectedRange?.end.toDateString() === date.toDateString();
      const divs = getDividendsForDate(date);
      const tradingInfo = getTradingDayInfo(date);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;

      // Separate announced and estimated dividends
      const announcedDivs = divs.filter(d =>
        d.source === 'alpaca_announced' || d.source === 'yahoo_announced'
      );
      const estimatedDivs = divs.filter(d =>
        d.source === 'yfinance_estimated'
      );

      let dayClass = "calendar-day";
      if (isToday) dayClass += " today";
      if (isInRange) dayClass += " in-range";
      if (isRangeStart) dayClass += " range-start";
      if (isRangeEnd) dayClass += " range-end";
      if (isWeekend) dayClass += " weekend";

      days.push(
        <div
          key={day}
          className={dayClass}
          onClick={(e) => handleDateClick(date, e)}
        >
          <div className="day-number">{day}</div>

          {/* Announced indicators row */}
          {(showExDates && announcedDivs.length > 0) ||
           (showBuyOrders && tradingInfo.announcedBuyCount > 0) ||
           (showSellOrders && tradingInfo.announcedSellCount > 0) ? (
            <div className="day-indicators">
              {showExDates && announcedDivs.length > 0 && (
                <div className="indicator-wrapper">
                  <div className="indicator ex-date-indicator">
                    <span className="indicator-count">{announcedDivs.length}</span>
                  </div>
                </div>
              )}

              {showBuyOrders && tradingInfo.announcedBuyCount > 0 && (
                <div className="indicator-wrapper">
                  <div className="indicator buy-indicator">
                    <span className="indicator-count">
                      {tradingInfo.announcedBuyCount}
                    </span>
                  </div>
                </div>
              )}

              {showSellOrders && tradingInfo.announcedSellCount > 0 && (
                <div className="indicator-wrapper">
                  <div className="indicator sell-indicator">
                    <span className="indicator-count">
                      {tradingInfo.announcedSellCount}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Estimated indicators row */}
          {showEstimates && (
            (showExDates && estimatedDivs.length > 0) ||
            (showBuyOrders && tradingInfo.estimatedBuyCount > 0) ||
            (showSellOrders && tradingInfo.estimatedSellCount > 0)
          ) ? (
            <div className="day-indicators">
              {showExDates && estimatedDivs.length > 0 && (
                <div className="indicator-wrapper">
                  <div className="indicator ex-date-indicator estimated">
                    <span className="indicator-count">{estimatedDivs.length}</span>
                  </div>
                </div>
              )}

              {showBuyOrders && tradingInfo.estimatedBuyCount > 0 && (
                <div className="indicator-wrapper">
                  <div className="indicator buy-indicator estimated">
                    <span className="indicator-count">
                      {tradingInfo.estimatedBuyCount}
                    </span>
                  </div>
                </div>
              )}

              {showSellOrders && tradingInfo.estimatedSellCount > 0 && (
                <div className="indicator-wrapper">
                  <div className="indicator sell-indicator estimated">
                    <span className="indicator-count">
                      {tradingInfo.estimatedSellCount}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      );
    }

    return days;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
    clearSelection();
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
    clearSelection();
  };

  const getDataSourceBadge = (source: string) => {
    if (source === "alpaca_announced") {
      return <span className="source-badge announced">Alpaca</span>;
    } else if (source === "yahoo_announced") {
      return <span className="source-badge announced">Yahoo</span>;
    } else if (source === "wsh_announced") {
      return <span className="source-badge wsh">WSH</span>;
    } else if (source === "yfinance_estimated") {
      return <span className="source-badge yfinance">Yahoo (Est.)</span>;
    } else if (source === "yfinance_historical") {
      return <span className="source-badge yfinance">Yahoo (Hist.)</span>;
    }
    return <span className="source-badge">{source}</span>;
  };

  const formatLastUpdated = (updatedAt?: string): string => {
    if (!updatedAt) return "Unknown";

    const updated = new Date(updatedAt);
    const now = new Date();
    const diffMs = now.getTime() - updated.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 60) {
      return diffMinutes <= 1 ? "Just now" : `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return updated.toLocaleDateString();
    }
  };

  const getTotalBuyOrders = () => {
    return rangeTrades.reduce((sum, day) => sum + day.buy_orders.length, 0);
  };

  const getTotalSellOrders = () => {
    return rangeTrades.reduce((sum, day) => sum + day.sell_orders.length, 0);
  };

  const getPrice = (symbol: string, date: string): number | null => {
    const key = `${symbol}_${date}`;
    return prices[key]?.price || null;
  };

  const calculateYield = (
    dividendAmount: number,
    price: number | null
  ): number | null => {
    if (!price || price === 0) return null;
    return (dividendAmount / price) * 100;
  };

  const monthName = currentDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="dividend-calendar-container">
      <div className="calendar-section">
        <div className="calendar-header">
          <button onClick={previousMonth}>&larr; Previous</button>
          <h2>{monthName}</h2>
          <button onClick={nextMonth}>Next &rarr;</button>
        </div>

        {monthSummary && (
          <div className="month-summary">
            <div className="summary-item">
              <span className="label">Trading Days:</span>
              <span className="value">{monthSummary.trading_days}</span>
            </div>
            <div className="summary-item">
              <span className="label">Buy Orders:</span>
              <span className="value buy">{monthSummary.total_buy_orders}</span>
            </div>
            <div className="summary-item">
              <span className="label">Sell Orders:</span>
              <span className="value sell">
                {monthSummary.total_sell_orders}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Dividend Events:</span>
              <span className="value">
                {monthSummary.total_dividend_events}
              </span>
            </div>
          </div>
        )}

        <div className="calendar-legend">
          <div
            className={`legend-item ${showExDates ? "active" : "inactive"}`}
            onClick={() => setShowExDates(!showExDates)}
          >
            <span className="legend-indicator ex-date-indicator"></span>
            <span className="legend-label">Ex-Dividend Dates</span>
          </div>
          <div
            className={`legend-item ${showBuyOrders ? "active" : "inactive"}`}
            onClick={() => setShowBuyOrders(!showBuyOrders)}
          >
            <span className="legend-indicator buy-indicator"></span>
            <span className="legend-label">Buy Orders</span>
          </div>
          <div
            className={`legend-item ${showSellOrders ? "active" : "inactive"}`}
            onClick={() => setShowSellOrders(!showSellOrders)}
          >
            <span className="legend-indicator sell-indicator"></span>
            <span className="legend-label">Sell Orders</span>
          </div>
          <div
            className={`legend-item ${showEstimates ? "active" : "inactive"}`}
            onClick={() => setShowEstimates(!showEstimates)}
            title="Show Yahoo Finance estimated dividends based on historical patterns"
          >
            <span className="legend-indicator estimate-indicator"></span>
            <span className="legend-label">Show Estimates</span>
          </div>
        </div>

        <div className="calendar-weekdays">
          <div>Sun</div>
          <div>Mon</div>
          <div>Tue</div>
          <div>Wed</div>
          <div>Thu</div>
          <div>Fri</div>
          <div>Sat</div>
        </div>

        <div className="calendar-grid">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            renderCalendarDays()
          )}
        </div>

        <div className="selection-hint">
          {!selectedRange &&
            "Click a date to select it, or shift-click to select a range"}
          {selectedRange && (
            <>
              {selectedRange.start.toDateString() ===
              selectedRange.end.toDateString()
                ? "Single date selected. Shift-click another date to create a range."
                : "Range selected. Click to select a single date, or shift-click to change range."}{" "}
              <button onClick={clearSelection} className="clear-btn">
                Clear Selection
              </button>
            </>
          )}
        </div>
      </div>

      {selectedRange && (
        <div className="details-panel">
          <div className="details-header">
            <h3>
              {selectedRange.start.toLocaleDateString()}
              {selectedRange.start.toDateString() !==
                selectedRange.end.toDateString() &&
                ` - ${selectedRange.end.toLocaleDateString()}`}
            </h3>
            <button onClick={clearSelection} className="close-btn">
              ✕
            </button>
          </div>

          <div className="range-summary">
            <div className="summary-stat">
              <div className="stat-value">{rangeDividends.length}</div>
              <div className="stat-label">Dividend Events</div>
            </div>
            <div className="summary-stat">
              <div className="stat-value">{getTotalBuyOrders()}</div>
              <div className="stat-label">Buy Orders</div>
            </div>
            <div className="summary-stat">
              <div className="stat-value">{getTotalSellOrders()}</div>
              <div className="stat-label">Sell Orders</div>
            </div>
          </div>

          {/* Dividend Events Section */}
          {rangeDividends.length > 0 && (
            <div className="detail-section">
              <h4>📅 Dividend Events ({rangeDividends.length})</h4>
              <div className="dividends-list">
                {rangeDividends.map((div, idx) => {
                  const price = getPrice(div.symbol, div.ex_date);
                  const yieldPct = calculateYield(div.dividend_amount, price);

                  return (
                    <div key={idx} className="dividend-item">
                      <div className="dividend-main">
                        <span className="symbol">{div.symbol}</span>
                        <span className="amount">
                          ${div.dividend_amount.toFixed(4)}
                          {yieldPct !== null && (
                            <span className="yield-pct">
                              {" "}
                              ({yieldPct.toFixed(2)}%)
                            </span>
                          )}
                        </span>
                        {getDataSourceBadge(div.source)}
                      </div>
                      <div className="dividend-details">
                        <span className="detail-label">Ex-Date:</span>
                        <span className="detail-value">{div.ex_date}</span>
                        {price !== null && (
                          <>
                            <span className="detail-label">Price:</span>
                            <span className="detail-value">
                              ${price.toFixed(2)}
                            </span>
                          </>
                        )}
                        {div.frequency && (
                          <>
                            <span className="detail-label">Frequency:</span>
                            <span className="detail-value frequency">
                              {div.frequency}
                            </span>
                          </>
                        )}
                        {div.payment_date && (
                          <>
                            <span className="detail-label">Pay Date:</span>
                            <span className="detail-value">
                              {div.payment_date}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="dividend-metadata">
                        <span className="metadata-text">
                          {div.source.replace(/_/g, ' ')} • Updated {formatLastUpdated(div.updated_at)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trading Actions Section */}
          {rangeTrades.length > 0 && (
            <div className="detail-section">
              <h4>💼 Trading Actions</h4>
              {rangeTrades.map((day, idx) => (
                <div key={idx} className="trading-day-item">
                  <div className="day-header">
                    <strong>{new Date(day.date).toLocaleDateString()}</strong>
                    <span className="badge">{day.total_trades} trades</span>
                  </div>

                  {day.buy_orders.length > 0 && (
                    <div className="orders-group">
                      <div className="orders-header buy">
                        Buy Orders ({day.buy_orders.length})
                      </div>
                      {day.buy_orders.map((order, orderIdx) => {
                        const price = getPrice(order.symbol, order.ex_date);
                        const yieldPct = calculateYield(
                          order.dividend_amount,
                          price
                        );

                        return (
                          <div key={orderIdx} className="trade-order buy-order">
                            <div className="order-main">
                              <span className="symbol">{order.symbol}</span>
                              <span className="dividend">
                                ${order.dividend_amount.toFixed(4)}
                                {yieldPct !== null && (
                                  <span className="yield-pct">
                                    {" "}
                                    ({yieldPct.toFixed(2)}%)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="order-details">
                              <span className="detail">
                                Ex: {order.ex_date}
                              </span>
                              {price !== null && (
                                <span className="detail">
                                  Price: ${price.toFixed(2)}
                                </span>
                              )}
                              <span className="detail">
                                Exit: {order.exit_date}
                              </span>
                              {order.frequency && (
                                <span className="frequency-badge">
                                  {order.frequency}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {day.sell_orders.length > 0 && (
                    <div className="orders-group">
                      <div className="orders-header sell">
                        Sell Orders ({day.sell_orders.length})
                      </div>
                      {day.sell_orders.map((order, orderIdx) => {
                        const price = getPrice(order.symbol, order.ex_date);
                        const yieldPct = calculateYield(
                          order.dividend_amount,
                          price
                        );

                        return (
                          <div
                            key={orderIdx}
                            className="trade-order sell-order"
                          >
                            <div className="order-main">
                              <span className="symbol">{order.symbol}</span>
                              <span className="dividend">
                                ${order.dividend_amount.toFixed(4)}
                                {yieldPct !== null && (
                                  <span className="yield-pct">
                                    {" "}
                                    ({yieldPct.toFixed(2)}%)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="order-details">
                              <span className="detail">
                                Ex: {order.ex_date}
                              </span>
                              {price !== null && (
                                <span className="detail">
                                  Price: ${price.toFixed(2)}
                                </span>
                              )}
                              <span className="detail">
                                Entry: {order.entry_date}
                              </span>
                              {order.frequency && (
                                <span className="frequency-badge">
                                  {order.frequency}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {rangeDividends.length === 0 && rangeTrades.length === 0 && (
            <div className="no-data">
              <p>No dividend events or trades in this range.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DividendCalendar;
